import { MjmlColumn, MjmlSection, MjmlText } from "@faire/mjml-react";
import { OrderSummaryDataType } from "@redotech/redo-model/advanced-flow/type-system/data-types/trackable-types";
import {
  Alignment,
  CartLayoutType,
  Section,
  SharedProps,
} from "@redotech/redo-model/email-builder";
import { memo } from "react";
import { EmailLineItem } from "./email-line-item";
import { EmailFulfillmentSummary } from "./fulfillment-summary";
import { CostSummarySection } from "./order-summary/cost-summary-section";
import { CustomerInformationSection } from "./order-summary/customer-information-section";
import { OrderDetailSection } from "./order-summary/order-details-section";
import { PaymentInformationSection } from "./order-summary/payment-information-section";

export type OrderSummaryState = Section.TrackableSummary & SharedProps;

export const OrderTableHeader = memo(function OrderTableHeader({
  title,
  borderBottom,
}: {
  title: string;
  borderBottom?: { color: string; padding: number };
}) {
  return (
    <>
      <div style={{ fontSize: "1.2rem", fontWeight: "bold" }}>{title}</div>
      {borderBottom && (
        <hr
          style={{
            marginTop: borderBottom.padding,
            borderBottomColor: borderBottom.color,
            opacity: 0.3,
          }}
        />
      )}
    </>
  );
});

export const EmailOrderSummary = memo(function emailOrderSummary(
  props: OrderSummaryState,
) {
  const orderSummary = props.schemaInstance[
    props.schemaFieldName!
  ] as OrderSummaryDataType["Trackable Summary"];

  if (!orderSummary) {
    if (props.isBuilder) {
      return null;
    } else {
      throw new Error("schemaFieldName is required");
    }
  }

  const {
    trackableType,
    orderDetails,
    costSummary,
    customerInformation,
    relevantLineItems,
  } = orderSummary;

  const padding = props.sectionPadding;

  return (
    <MjmlSection
      backgroundColor={props.sectionColor}
      paddingBottom={padding.bottom}
      paddingLeft={padding.left}
      paddingRight={padding.right}
      paddingTop={padding.top}
    >
      <MjmlColumn>
        <MjmlText color={props.textColor} fontFamily={props.fontFamily}>
          {props.showOrderInformation && (
            <div>
              <OrderTableHeader title={`${trackableType.titleCase} details:`} />
              <OrderDetailSection
                orderDate={orderDetails.orderDate}
                orderId={orderDetails.orderId}
              />
              <hr style={{ marginTop: "16px", marginBottom: "16px" }} />
            </div>
          )}
          {!orderDetails.fulfilled ? (
            <>
              <OrderTableHeader title={`Items ${trackableType.verb}:`} />
              <>
                {relevantLineItems.map((item, index) => (
                  <EmailLineItem
                    alignment={Alignment.LEFT}
                    format={props.format}
                    imageCornerRadius={props.imageCornerRadius}
                    imageSize={props.imageSize}
                    isBuilder={props.isBuilder}
                    isLastInRow={index === relevantLineItems.length - 1}
                    item={item}
                    key={index}
                    showDummyImages={props.isBuilder}
                    showImage
                    showPrice
                    showProperties
                    showQuantity
                    showTitle
                    showVariant
                    stackType={CartLayoutType.ROWS}
                    team={props.team}
                    textColor={props.textColor}
                    utm={props.utm}
                  />
                ))}
              </>
            </>
          ) : (
            <EmailFulfillmentSummary {...props} />
          )}
          {props.showCostSummary && (
            <div>
              <hr style={{ marginTop: "16px", marginBottom: "16px" }} />
              <OrderTableHeader title="Summary" />
              <CostSummarySection {...costSummary} />
            </div>
          )}
          {props.showPaymentInformation && (
            <div>
              <hr style={{ marginTop: "16px", marginBottom: "16px" }} />
              {/* Card number has never worked, but it's also not an option in the
          UI right now so no reason to fix it */}
              <PaymentInformationSection
                cardInfo={`XXXXXXXXXXXX${"5678"}`}
                total="$X.XX"
              />
            </div>
          )}
          {props.showCustomerInformation && (
            <div>
              <hr style={{ marginTop: "16px", marginBottom: "16px" }} />
              <OrderTableHeader title="Customer information" />
              <CustomerInformationSection
                billingAddress={customerInformation.billingAddress}
                shippingAddress={customerInformation.shippingAddress}
              />
            </div>
          )}
        </MjmlText>
      </MjmlColumn>
    </MjmlSection>
  );
});
